// Fixed LoadingOverlayExamples.tsx
import React, { useState, useEffect } from 'react';
import { Button, Card, CardBody, CardHeader, Row, Col, Badge } from 'reactstrap';
import LoadingOverlay from './LoadingOverlay';

interface ExampleState {
    basic: boolean;
    customText: boolean;
    large: boolean;
    small: boolean;
    customColor: boolean;
    noBlur: boolean;
    customOpacity: boolean;
    processing: boolean;
    uploading: boolean;
    saving: boolean;
    rotating: boolean;
}

const LoadingOverlayExamples: React.FC = () => {
    const [examples, setExamples] = useState<ExampleState>({
        basic: false,
        customText: false,
        large: false,
        small: false,
        customColor: false,
        noBlur: false,
        customOpacity: false,
        processing: false,
        uploading: false,
        saving: false,
        rotating: false
    });

    const toggleExample = (key: keyof ExampleState) => {
        setExamples(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const showExample = (key: keyof ExampleState, duration = 3000) => {
        setExamples(prev => ({ ...prev, [key]: true }));
        setTimeout(() => {
            setExamples(prev => ({ ...prev, [key]: false }));
        }, duration);
    };

    const hideAllExamples = () => {
        setExamples({
            basic: false,
            customText: false,
            large: false,
            small: false,
            customColor: false,
            noBlur: false,
            customOpacity: false,
            processing: false,
            uploading: false,
            saving: false,
            rotating: false
        });
    };

    // Check if any overlay is currently active
    const hasActiveOverlay = Object.values(examples).some(Boolean);

    // Add/remove body class to prevent scrolling when overlay is active
    useEffect(() => {
        if (hasActiveOverlay) {
            document.body.classList.add('loading-overlay-active');
        } else {
            document.body.classList.remove('loading-overlay-active');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('loading-overlay-active');
        };
    }, [hasActiveOverlay]);

    const exampleItems = [
        {
            key: 'basic' as keyof ExampleState,
            title: 'Basic Usage',
            description: 'Simple loading overlay with default settings',
            code: '<LoadingOverlay isVisible={loading} />',
            component: (
                <LoadingOverlay isVisible={examples.basic} />
            )
        },
        {
            key: 'customText' as keyof ExampleState,
            title: 'Custom Text',
            description: 'Loading overlay with custom text and subtext',
            code: `<LoadingOverlay 
  isVisible={saving}
  text="Saving Changes"
  subtext="Please wait..."
/>`,
            component: (
                <LoadingOverlay
                    isVisible={examples.customText}
                    text="Saving Changes"
                    subtext="Please wait..."
                />
            )
        },
        {
            key: 'large' as keyof ExampleState,
            title: 'Large Size',
            description: 'Large sized overlay for important operations',
            code: `<LoadingOverlay 
  isVisible={uploading}
  text="Uploading Files"
  size="large"
/>`,
            component: (
                <LoadingOverlay
                    isVisible={examples.large}
                    text="Uploading Files"
                    subtext="Processing your documents..."
                    size="large"
                />
            )
        },
        {
            key: 'small' as keyof ExampleState,
            title: 'Small Size',
            description: 'Compact overlay for quick operations',
            code: `<LoadingOverlay 
  isVisible={loading}
  text="Loading Data"
  size="small"
/>`,
            component: (
                <LoadingOverlay
                    isVisible={examples.small}
                    text="Loading Data"
                    subtext="Fetching information..."
                    size="small"
                />
            )
        },
        {
            key: 'customColor' as keyof ExampleState,
            title: 'Custom Color',
            description: 'Green spinner for success operations',
            code: `<LoadingOverlay 
  isVisible={processing}
  text="Processing Payment"
  spinnerColor="#28a745"
/>`,
            component: (
                <LoadingOverlay
                    isVisible={examples.customColor}
                    text="Processing Payment"
                    subtext="Secure transaction in progress..."
                    spinnerColor="#28a745"
                />
            )
        },
        {
            key: 'noBlur' as keyof ExampleState,
            title: 'No Blur Effect',
            description: 'Overlay without background blur',
            code: `<LoadingOverlay 
  isVisible={loading}
  text="Loading Data"
  noBlur={true}
/>`,
            component: (
                <LoadingOverlay
                    isVisible={examples.noBlur}
                    text="Loading Data"
                    subtext="No blur effect applied"
                    noBlur={true}
                />
            )
        },
        {
            key: 'customOpacity' as keyof ExampleState,
            title: 'Custom Opacity',
            description: 'Higher opacity background for better visibility',
            code: `<LoadingOverlay 
  isVisible={loading}
  text="Important Process"
  backgroundOpacity={0.9}
/>`,
            component: (
                <LoadingOverlay
                    isVisible={examples.customOpacity}
                    text="Important Process"
                    subtext="High opacity background"
                    backgroundOpacity={0.9}
                />
            )
        },
        {
            key: 'processing' as keyof ExampleState,
            title: 'Processing Scenario',
            description: 'Real-world example for data processing',
            code: `<LoadingOverlay 
  isVisible={processing}
  text="Analyzing Job Requirements"
  subtext="Finding qualified contractors..."
  spinnerColor="#3577f1"
/>`,
            component: (
                <LoadingOverlay
                    isVisible={examples.processing}
                    text="Analyzing Job Requirements"
                    subtext="Finding qualified contractors in your area..."
                    spinnerColor="#3577f1"
                />
            )
        },
        {
            key: 'uploading' as keyof ExampleState,
            title: 'File Upload Scenario',
            description: 'Example for file upload operations',
            code: `<LoadingOverlay 
  isVisible={uploading}
  text="Uploading Documents"
  subtext="Processing attachments..."
  size="large"
  spinnerColor="#17a2b8"
/>`,
            component: (
                <LoadingOverlay
                    isVisible={examples.uploading}
                    text="Uploading Documents"
                    subtext="Processing your project attachments..."
                    size="large"
                    spinnerColor="#17a2b8"
                />
            )
        },
        {
            key: 'saving' as keyof ExampleState,
            title: 'Save Operation',
            description: 'Example for save operations',
            code: `<LoadingOverlay 
  isVisible={saving}
  text="Saving Profile"
  subtext="Updating your information..."
  spinnerColor="#fd7e14"
/>`,
            component: (
                <LoadingOverlay
                    isVisible={examples.saving}
                    text="Saving Profile"
                    subtext="Updating your information..."
                    spinnerColor="#fd7e14"
                />
            )
        },
        {
            key: 'rotating' as keyof ExampleState,
            title: 'Rotating Example',
            description: 'Example for rotating texts',
            code: `<LoadingOverlay 
            isVisible={examples.rotating}
            size="large"
            backgroundOpacity={0.9}
            text={["BidBro", "BidBro", "BidBro"]}
            subtext={["Demo: Hiring Contractors", "Hiring Contractors Sucks!", "...But it doesn't have to!"]}
            textInterval={500}
            spinnerColor="#fd7e14"
          />`,
            component: (
                <LoadingOverlay
                    isVisible={examples.rotating}
                    size="large"
                    backgroundOpacity={0.6}
                    text={["BidBro", "BidBro", "BidBro", "BidBro"]}
                    subtext={["Hiring Contractors Demo", "Hiring Contractors Sucks!", "...But it doesn't have to!", "Visit www.BidBro.com today to post your job!"]}
                    textInterval={5000}
                />
            )
        }
    ];

    return (
        <div className="loading-overlay-examples">
            {/* Render all overlay components */}
            {exampleItems.map(item => item.component)}

            {/* Fixed position controls with HIGHER z-index than overlay */}
            {hasActiveOverlay && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    zIndex: 10000000, // Higher than overlay's 999999
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                }}>
                    <Button
                        color="danger"
                        size="sm"
                        onClick={hideAllExamples}
                        className="shadow-lg"
                        style={{
                            minWidth: '100px',
                            fontWeight: 'bold',
                            position: 'relative',
                            zIndex: 10000001 // Even higher for individual buttons
                        }}
                    >
                        <i className="ri-close-line me-1"></i>
                        Hide All
                    </Button>

                    {/* Individual hide buttons for active overlays */}
                    {Object.entries(examples).map(([key, isActive]) => {
                        if (!isActive) return null;
                        const item = exampleItems.find(item => item.key === key);
                        if (!item) return null;

                        return (
                            <Button
                                key={key}
                                color="warning"
                                size="sm"
                                onClick={() => toggleExample(key as keyof ExampleState)}
                                className="shadow-lg"
                                style={{
                                    minWidth: '100px',
                                    fontSize: '0.75rem',
                                    position: 'relative',
                                    zIndex: 10000001
                                }}
                            >
                                <i className="ri-stop-line me-1"></i>
                                Hide {item.title}
                            </Button>
                        );
                    })}
                </div>
            )}

            <Row>
                <Col lg={12}>
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="card-title mb-0">LoadingOverlay Examples</h4>
                                <p className="text-muted mb-0">Interactive demonstrations of the LoadingOverlay component</p>
                            </div>
                            {!hasActiveOverlay && (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={hideAllExamples}
                                    disabled={true}
                                >
                                    Hide All
                                </Button>
                            )}
                        </CardHeader>
                        <CardBody>
                            <Row>
                                {exampleItems.map((item, index) => (
                                    <Col lg={6} xl={4} key={item.key} className="mb-4">
                                        <Card className="h-100 border">
                                            <CardBody>
                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                    <h6 className="card-title text-primary mb-0">{item.title}</h6>
                                                    <Badge
                                                        color={examples[item.key] ? 'success' : 'secondary'}
                                                        className="ms-2"
                                                    >
                                                        {examples[item.key] ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </div>

                                                <p className="text-muted small mb-3">{item.description}</p>

                                                <div className="bg-light p-3 rounded mb-3">
                                                    <code className="small text-wrap" style={{ fontSize: '0.75rem' }}>
                                                        {item.code}
                                                    </code>
                                                </div>

                                                <div className="d-flex gap-2">
                                                    <Button
                                                        color={examples[item.key] ? 'success' : 'primary'}
                                                        size="sm"
                                                        onClick={() => toggleExample(item.key)}
                                                        className="flex-fill"
                                                    >
                                                        {examples[item.key] ? (
                                                            <>
                                                                <i className="ri-check-line me-1"></i>
                                                                Active
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-play-line me-1"></i>
                                                                Show
                                                            </>
                                                        )}
                                                    </Button>

                                                    <Button
                                                        color="info"
                                                        size="sm"
                                                        onClick={() => showExample(item.key)}
                                                        disabled={examples[item.key]}
                                                    >
                                                        <i className="ri-time-line me-1"></i>
                                                        3s Demo
                                                    </Button>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            <Row className="mt-4">
                                <Col lg={12}>
                                    <Card className="bg-light border-0">
                                        <CardBody>
                                            <h6 className="text-primary mb-3">
                                                <i className="ri-information-line me-2"></i>
                                                Usage Notes
                                            </h6>
                                            <Row>
                                                <Col md={6}>
                                                    <ul className="list-unstyled mb-0">
                                                        <li className="mb-2">
                                                            <i className="ri-check-line text-success me-2"></i>
                                                            <strong>Toggle:</strong> Click "Show/Active" to manually control overlays
                                                        </li>
                                                        <li className="mb-2">
                                                            <i className="ri-check-line text-success me-2"></i>
                                                            <strong>Demo:</strong> Click "3s Demo" for automatic 3-second display
                                                        </li>
                                                        <li className="mb-2">
                                                            <i className="ri-check-line text-success me-2"></i>
                                                            <strong>Multiple:</strong> You can show multiple overlays simultaneously
                                                        </li>
                                                    </ul>
                                                </Col>
                                                <Col md={6}>
                                                    <ul className="list-unstyled mb-0">
                                                        <li className="mb-2">
                                                            <i className="ri-check-line text-success me-2"></i>
                                                            <strong>Controls:</strong> Hide buttons appear in top-right when overlays are active
                                                        </li>
                                                        <li className="mb-2">
                                                            <i className="ri-check-line text-success me-2"></i>
                                                            <strong>Accessible:</strong> Proper contrast and alt text included
                                                        </li>
                                                        <li className="mb-2">
                                                            <i className="ri-check-line text-success me-2"></i>
                                                            <strong>Customizable:</strong> Colors, sizes, and text are configurable
                                                        </li>
                                                    </ul>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default LoadingOverlayExamples;